import { UiBox, UiSelect, UiTypography } from "@smp/ui";
import { textCapitalize, unicodeToUnified } from "@smp/utils";
import clsx from "clsx";
import { FC, useMemo } from "react";

import { GPT_LANGUAGES } from "@/fixtures";
import { useEmoji } from "@/hooks/use_emoji";

import classes from "./gpt_language_select.module.css";

export interface IUiGptLanguageSelectProps {
  value: string;
  emojiSize?: "large";
  onChange: (lang: string) => void;
  isDisabled?: boolean;
}

export const UiGptLanguageSelect: FC<IUiGptLanguageSelectProps> = ({
  value,
  onChange,
  emojiSize,
  isDisabled,
}) => {
  const {
    search: emojiSearch,
    data: emojiData,
    getImageUrl: getEmojiImg,
  } = useEmoji({ enabled: true });

  const options = useMemo(
    () =>
      GPT_LANGUAGES.map((lang) => {
        const languageName = textCapitalize(lang.getDisplayName());
        const emojiImage =
          emojiSearch &&
          lang.unicode.replace(emojiSearch, (unicode: string) => {
            const unified = unicodeToUnified(unicode);
            const src = getEmojiImg(unified, 32);

            if (src && emojiData?.emojis[unified]?.vendors.includes("apple")) {
              return `<img alt="${unicode}" src="${src}" class="${clsx(
                classes.emoji,
                classes[`emoji_${emojiSize}`],
              )}" />`;
            }

            return unicode;
          });
        return {
          value: lang.id,
          label: (
            <UiBox
              component="span"
              display="flex"
              alignItems={"center"}
              width={140}
            >
              {emojiImage && (
                <UiBox
                  component="span"
                  dangerouslySetInnerHTML={{ __html: emojiImage }}
                />
              )}
              <UiTypography component="span">{languageName}</UiTypography>
            </UiBox>
          ),
        };
      }).sort((option1, option2) => (option1.label > option2.label ? 1 : -1)),
    [emojiData?.emojis, emojiSearch, emojiSize, getEmojiImg],
  );

  return (
    <UiSelect
      fullWidth
      value={value}
      isSortDisabled
      options={options}
      onChange={(e) => onChange(e.target.value as string)}
      disabled={isDisabled}
    />
  );
};
